import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { JobDTO } from "../../api";
import { useJobActions } from "./JobCenterProvider";

/**
 * Starts a document export and hands it to the job center.
 *
 * Exports used to be a blocking download: the browser held an open request for
 * however long the report took to build, and a reload or a proxy timeout lost
 * it. Now the server answers immediately with a job, the job center polls it,
 * announces it when it finishes and offers the file — so the operator can keep
 * working, and a slow report is watched rather than waited on.
 *
 * `busy` still exists for the button: it covers the submit round-trip only,
 * which is short, and stops a double click queueing the same export twice.
 */
export function useExport() {
  const { refresh, setOpen } = useJobActions();
  const { t } = useTranslation();
  const [busy, setBusy] = useState<string | null>(null);

  const start = async (
    key: string,
    request: () => Promise<JobDTO>,
    errorMessage = "Export failed",
  ): Promise<void> => {
    setBusy(key);
    try {
      const job = await request();
      // The job center is the one place that knows how the export ends, so the
      // toast points there rather than trying to report progress itself.
      refresh();
      toast.success(job.label, {
        description: t("job.queued", "Export started. You will be notified when it is ready."),
        action: { label: t("job.view", "View"), onClick: () => setOpen(true) },
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : errorMessage);
    } finally {
      setBusy(null);
    }
  };

  return { start, busy, isBusy: (key: string) => busy === key };
}
